import { api } from './client';
import { trackEvent } from '../lib/analytics';
import {
  Conversion,
  ConversionListResponse,
  ConversionReport,
  ConversionStatus,
  ReportReason,
} from '../types';

export interface CreateConversionRequest {
  url: string;
  title?: string;
}

export interface ConversionStatusResponse {
  id: string;
  status: ConversionStatus;
  progress: number;
  errorMessage?: string;
}

export const conversionsApi = {
  /**
   * Create a new conversion from a URL
   */
  createConversion: async (
    url: string,
    title?: string
  ): Promise<Conversion> => {
    return api.post<Conversion>('/v1/conversions/', { url, title });
  },

  /**
   * Create a new conversion from raw pasted text. When `title` is omitted
   * (or blank), the backend derives one from the first non-empty line of
   * content.
   */
  createTextConversion: async (
    content: string,
    title?: string
  ): Promise<Conversion> => {
    return api.post<Conversion>('/v1/conversions/text', { content, title });
  },

  /**
   * Get all conversions with pagination
   */
  getConversions: async (
    limit: number = 10,
    offset: number = 0
  ): Promise<ConversionListResponse> => {
    return api.get<ConversionListResponse>('/v1/conversions/', { limit, offset });
  },

  /**
   * Get a single conversion by ID
   */
  getConversion: async (id: string): Promise<Conversion> => {
    return api.get<Conversion>(`/v1/conversions/${id}`);
  },

  /**
   * Get the status of a conversion
   */
  getConversionStatus: async (id: string): Promise<ConversionStatusResponse> => {
    return api.get<ConversionStatusResponse>(`/v1/conversions/${id}/status`);
  },

  /**
   * Delete a conversion
   */
  deleteConversion: async (id: string): Promise<void> => {
    return api.delete<void>(`/v1/conversions/${id}`);
  },

  /**
   * Get the audio URL for a completed conversion
   */
  getAudioUrl: async (id: string): Promise<{ audioUrl: string }> => {
    return api.get<{ audioUrl: string }>(`/v1/conversions/${id}/audio`);
  },

  /**
   * Get a public share URL for a completed conversion.
   * Mirrors the get_share_link MCP tool — anyone with the URL can listen.
   */
  getShareLink: async (id: string): Promise<{ shareUrl: string }> => {
    return api.get<{ shareUrl: string }>(`/v1/conversions/${id}/share`);
  },

  /**
   * Regenerate the audio of an expired edition at no cost. The conversion
   * goes back to "queued" and completes again through the normal pipeline.
   */
  renewConversion: async (id: string): Promise<Conversion> => {
    return api.post<Conversion>(`/v1/conversions/${id}/renew`);
  },

  /**
   * Flag a conversion as failed or poor quality. It lands in the admin
   * review inbox. Each conversion can be reported once (409 on repeat).
   */
  reportConversion: async (
    id: string,
    reason: ReportReason,
    details?: string
  ): Promise<ConversionReport> => {
    return api.post<ConversionReport>(`/v1/conversions/${id}/report`, {
      reason,
      details: details?.trim() || undefined,
    });
  },
};

/**
 * Build a filesystem-safe MP3 filename from an edition's title.
 * Strips control + OS-reserved chars, collapses whitespace, trims
 * leading/trailing dots, and caps length so the download doesn't blow
 * past Windows' 255-byte path limit.
 */
export function buildAudioFilename(title: string | null | undefined): string {
  const fallback = 'noctua-edition';
  const cleaned = (title ?? '')
    .toString()
    // eslint-disable-next-line no-control-regex
    .replace(new RegExp('[\x00-\x1f\x7f]', 'g'), ' ')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[.\s-]+|[.\s-]+$/g, '')
    .slice(0, 120)
    .trim();
  return `${cleaned || fallback}.mp3`;
}

/**
 * Fetch the signed audio URL, download the MP3 as a blob, and save it
 * with a title-derived filename. Returns the filename used.
 */
export async function downloadConversion(
  id: string,
  title?: string | null
): Promise<string> {
  const { audioUrl } = await conversionsApi.getAudioUrl(id);
  const response = await fetch(audioUrl);
  if (!response.ok) {
    throw new Error(`Audio download failed (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  const filename = buildAudioFilename(title);
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
  return filename;
}

export type ShareResult = 'shared' | 'copied' | 'cancelled';

/**
 * Fetch a public share URL for a conversion and either invoke the native
 * Web Share sheet (preferred on mobile) or copy the URL to the clipboard.
 *
 * We deliberately share the URL rather than the audio file: the public
 * /p/{slug} share page carries Open Graph tags, so messaging apps like
 * WhatsApp render a rich preview card (title, summary, cover art) for the
 * link. A shared file produces no such preview. Users who want the raw MP3
 * have the dedicated Download action.
 */
export async function shareConversion(
  id: string,
  title?: string | null
): Promise<ShareResult> {
  const { shareUrl } = await conversionsApi.getShareLink(id);
  const data: ShareData = {
    title: title ?? 'Listen on noctua',
    text: title ? `Listen to "${title}" on noctua` : 'Listen on noctua',
    url: shareUrl,
  };

  if (
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    (!navigator.canShare || navigator.canShare(data))
  ) {
    try {
      await navigator.share(data);
      trackEvent('share_link_copied', { method: 'shared' });
      return 'shared';
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return 'cancelled';
      }
      // Other errors fall through to clipboard
    }
  }

  await navigator.clipboard.writeText(shareUrl);
  trackEvent('share_link_copied', { method: 'copied' });
  return 'copied';
}

export default conversionsApi;
